<script setup lang="ts">
    import { onMounted, reactive, ref, inject } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import { OAuthProvider } from './OAuthProvider.types';
    import { getOAuthProvider, saveOAuthProvider, getOAuthProviders } from './OAuthProvider.access';
    import Loading from '@shared/components/Loading.vue';
    import { isValidUrl } from '@shared/utils/validators';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps, isEmpty } from '@shared/components/Form/Form';
    import { AccountListAccess } from '../Account/AccountList.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { getTriggerURI } from './OAuthProvider.helpers';
    import CopyButton from '@shared/components/CopyButton.vue';
    import { bsAlert } from '@shared/components/bsAlerts';
    const route = useRoute();
    const router = useRouter();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const accountListAccess: AccountListAccess = injectorAccess.injector.get(AccountListAccess);

    const state = reactive<{
        mode: 'edit' | 'new';
        isValid: boolean;
        validationMessages: string[];
        form: FormProps;
        oauthProvider: OAuthProvider | null;
        selectedAccounts: any[];
        filteredLimitedAccounts: any[];
    }>({
        mode: 'new',
        isValid: true,
        validationMessages: [],
        form: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        oauthProvider: null,
        selectedAccounts: [],
        filteredLimitedAccounts: [],
    });

    const isLoading = ref(false);

    const emit = defineEmits(['update:modelUpdated', 'cancel']);

    onMounted(async () => {
        if (route.params.oauthProviderId) {
            state.mode = 'edit';
            const oauthProviderId = route.params.oauthProviderId as string;
            await loadOAuthProvider(oauthProviderId).then(() => {
                state.selectedAccounts = [state.oauthProvider?.AccountId];
            });
        } else {
            state.mode = 'new';
            state.oauthProvider = {
                OAuthProviderId: '',
                ProviderName: '',
                BaseUri: '',
                ClientId: '',
                AllowProvisioning: false,
                ClientSecret: '',
                Scopes: 'openid email',
                TokenUri: '',
                CallbackUri: 'https://app.ungapped.com/account/oauthcallback',
                LogoUrl: '',
                AccountId: '',
                ClientCompanyId: '',
                CustomSubClaim: '',
            };
        }

        await accountListAccess.get();

        setupFormData();
    });

    const loadOAuthProvider = async (oauthProviderId: string) => {
        isLoading.value = true;
        try {
            state.oauthProvider = await getOAuthProvider(oauthProviderId);
        } catch (error) {
            state.form.alert = {
                type: 'danger',
                title: 'Oops!',
                content: 'Failed to load OAuth Provider.',
            };
        } finally {
            isLoading.value = false;
        }
    };

    const regexHost = '^(https:\\/\\/[a-zA-Z0-9_\\-\\.\\/]+|http:\\/\\/localhost[\\S]*)$';

    const setupFormData = () => {
        if (!state.oauthProvider) return;

        state.form.data = {
            ProviderName: {
                modelValue: state.oauthProvider.ProviderName,
                required: true,
                group: 'Basic settings',
                input: {
                    label: 'Provider Name',
                },
            },
            AccountId: {
                modelValue: state.selectedAccounts,
                required: true,
                isValid: state.selectedAccounts && state.selectedAccounts.length > 0 ? true : undefined,
                group: 'Basic settings',
                multiSelect: {
                    label: 'Account',
                    selectProp: 'AccountId',
                    displayProp: (account) => account.Name + ' (' + account.ClientCompanyName + ')',
                    selectOnly: 'true',
                    items: accountListAccess.state.filteredAccounts || [],
                    loading: accountListAccess.state.loading,
                    maxSelectable: 1,
                },
            },
            BaseUri: {
                modelValue: state.oauthProvider.BaseUri,
                required: true,
                isValid: state.oauthProvider.BaseUri && isValidUrl(state.oauthProvider.BaseUri) ? true : undefined,
                group: 'Uris',
                input: {
                    label: 'Base URI',
                    pattern: regexHost,
                },
            },
            ClientId: {
                modelValue: state.oauthProvider.ClientId,
                required: true,
                isValid: state.oauthProvider.ClientId ? true : undefined,
                group: 'Credentials',
                input: {
                    label: 'Client ID',
                },
            },
            AllowProvisioning: {
                modelValue: state.oauthProvider.AllowProvisioning,
                required: false,
                group: 'Provisioning',
                checkbox: {
                    label: 'Allow provisioning',
                },
            },
            ClientSecret: {
                modelValue: state.oauthProvider.ClientSecret,
                required: true,
                isValid: state.oauthProvider.ClientSecret ? true : undefined,
                group: 'Credentials',
                input: {
                    label: 'Client Secret',
                },
            },
            Scopes: {
                modelValue: state.oauthProvider.Scopes,
                required: true,
                isValid: state.oauthProvider.Scopes ? true : undefined,
                group: 'Options',
                input: {
                    label: 'Scopes',
                },
            },
            TokenUri: {
                modelValue: state.oauthProvider.TokenUri,
                required: true,
                isValid: state.oauthProvider.TokenUri && isValidUrl(state.oauthProvider.TokenUri) ? true : undefined,
                group: 'Uris',
                input: {
                    label: 'Token URI',
                    pattern: regexHost,
                },
            },
            CallbackUri: {
                modelValue: state.oauthProvider.CallbackUri,
                required: true,
                isValid: state.oauthProvider.CallbackUri && isValidUrl(state.oauthProvider.CallbackUri) ? true : undefined,
                group: 'Uris',
                input: {
                    label: 'Callback URI',
                    pattern: regexHost,
                },
            },
            LogoUrl: {
                modelValue: state.oauthProvider.LogoUrl,
                required: false,
                isValid: !state.oauthProvider.LogoUrl || isValidUrl(state.oauthProvider.LogoUrl) ? true : undefined,
                group: 'Uris',
                input: {
                    label: 'Logo URI',
                    pattern: regexHost,
                },
            },
            CustomSubClaim: {
                modelValue: state.oauthProvider.CustomSubClaim,
                required: false,
                group: 'Options',
                input: {
                    label: 'Custom Sub Claim',
                },
            },
        };
    };

    // Helper function to validate a URL field and set appropriate error message
    const validateUrlField = (fieldName: string, fieldValue: string, fieldLabel: string): boolean => {
        if (!fieldValue) {
            state.form.alert = {
                type: 'danger',
                title: 'Oops!',
                content: `${fieldLabel} is required.`,
            };
            return false;
        }

        if (!isValidUrl(fieldValue)) {
            state.form.alert = {
                type: 'danger',
                title: 'Oops!',
                content: `${fieldLabel} is not a valid URL.`,
            };
            return false;
        }

        return true;
    };

    const saveChanges = (event: Event, formData: any) => {
        if (isLoading.value === true) return;

        // Reset alert
        state.form.alert = null;

        // Required field validation
        if (!formData.ProviderName) {
            state.form.alert = {
                type: 'danger',
                title: 'Oops!',
                content: 'Provider Name is required.',
            };
            return;
        }

        if (!formData.AccountId || !formData.AccountId.length) {
            state.form.alert = {
                type: 'danger',
                title: 'Oops!',
                content: 'Account is required.',
            };
            return;
        }

        if (!formData.ClientId) {
            state.form.alert = {
                type: 'danger',
                title: 'Oops!',
                content: 'Client ID is required.',
            };
            return;
        }

        if (!formData.ClientSecret) {
            state.form.alert = {
                type: 'danger',
                title: 'Oops!',
                content: 'Client Secret is required.',
            };
            return;
        }

        if (!formData.Scopes) {
            state.form.alert = {
                type: 'danger',
                title: 'Oops!',
                content: 'Scopes is required.',
            };
            return;
        }

        // URL field validation
        if (!validateUrlField('BaseUri', formData.BaseUri, 'Base URI')) return;
        if (!validateUrlField('TokenUri', formData.TokenUri, 'Token URI')) return;
        if (!validateUrlField('CallbackUri', formData.CallbackUri, 'Callback URI')) return;

        // Only validate LogoUrl if it's provided
        if (formData.LogoUrl && !isValidUrl(formData.LogoUrl)) {
            state.form.alert = {
                type: 'danger',
                title: 'Oops!',
                content: 'Logo URI is not a valid URL.',
            };
            return;
        }

        isLoading.value = true;

        formData.OAuthProviderId = state.oauthProvider?.OAuthProviderId;
        let selectedAccountId: string = formData.AccountId[0];
        formData.AccountId = selectedAccountId;

        const selectedAccount = accountListAccess.state.filteredAccounts.find((a) => a.AccountId == selectedAccountId);
        if (selectedAccount) {
            formData.ClientCompanyId = selectedAccount.ClientCompanyId;
        }

        saveOAuthProvider(formData)
            .then((savedProvider) => {
                // Show success notification
                const isNew = state.mode === 'new';
                bsAlert({
                    type: 'success',
                    title: 'Success!',
                    content: `OAuth Provider ${isNew ? 'created' : 'updated'} successfully.`,
                    timeout: 5000,
                });

                if (isNew && savedProvider && savedProvider.OAuthProviderId) {
                    // Navigate to edit page with the new ID
                    router.push({
                        name: 'EditOAuthProvider',
                        params: { oauthProviderId: savedProvider.OAuthProviderId },
                    });
                } else if (savedProvider && savedProvider.OAuthProviderId) {
                    // For updates, we're already on the edit page, just reload the data
                    loadOAuthProvider(savedProvider.OAuthProviderId);
                }

                emit('update:modelUpdated', savedProvider);
            })
            .catch((error) => {
                state.form.alert = {
                    type: 'danger',
                    title: 'Oops!',
                    content: 'Failed to save changes.',
                };
            })
            .finally(() => {
                isLoading.value = false;
            });
    };

    const updateModelValue = (key: string, newVal: any) => {
        state.form.data[key].modelValue = newVal;

        // Update validation state for the field when its value changes
        if (key === 'BaseUri' || key === 'TokenUri' || key === 'CallbackUri') {
            state.form.data[key].isValid = newVal && isValidUrl(newVal) ? true : false;
        } else if (key === 'LogoUrl') {
            state.form.data[key].isValid = !newVal || isValidUrl(newVal) ? true : false;
        } else if (key === 'AccountId') {
            state.form.data[key].isValid = newVal && newVal.length > 0 ? true : false;
        } else if (state.form.data[key].required) {
            state.form.data[key].isValid = newVal ? true : false;
        }
    };
</script>

<template>
    <router-link :to="{ name: 'OAuthProviders' }">
        <span class="glyphicon glyphicon-chevron-left c-light" aria-hidden="true"></span>
        View all
    </router-link>
    <h4 v-if="state.mode == 'edit'">Edit OAuth Provider</h4>
    <h4 v-if="state.mode == 'new'">New OAuth Provider</h4>

    <Loading :show="isLoading" :fixed="true"></Loading>
    <div class="m-s-b" v-if="state.form.data.ProviderName">
        <CopyButton
            class="m-xs-l"
            :textToCopy="getTriggerURI(state.form?.data?.ProviderName.modelValue || '')"
            buttonText="Copy trigger URI"
            :tooltip="getTriggerURI(state.form?.data?.ProviderName.modelValue || '')"></CopyButton>
    </div>
    <div class="fieldset-grid">
        <Form
            class="fieldset-grid"
            v-if="!isEmpty(state.form.data)"
            :data="state.form.data"
            :alert="state.form.alert"
            :disableSubmit="state.form.disableSubmit"
            :cancel="() => $router.push({ name: 'OAuthProviders', params: {} })"
            @update:modelValue="updateModelValue"
            @onSubmit="saveChanges"></Form>
    </div>
</template>

<style>
    .m-s-b {
        margin-bottom: 3px;
    }
</style>

<style scoped>
    .fieldset-grid {
        max-width: 800px;
    }
</style>

<script lang="ts">
    export default {
        name: 'EditOAuthProvider',
    };
</script>
